import { useMemo } from "react";
import { Endpoints, queryKeys } from "../api";
import { useAuth, useQueryParams, useTerritories } from "../contexts";
import { getQueryType, getRequestOptions } from "../helper-functions";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import {
    Tissue,
    IUseQueryPaged,
    QueryHookParams,
    QueryType,
    QueryResult,
    LabDashboardData,
    QueryParams,
    DistroDashboardData,
    TissuePlacement,
    InitialEval,
    MutationResult,
    CommentUpdateRequest,
    DistroTissueSummary,
    Invoice,
    PagedResponse,
    Entity,
    DefaultPagedResponse,
    ProductCodeResponse,
} from "../models";
import { Operation } from "rfc6902";

interface IUseTissueQuery extends Pick<IUseQueryPaged<Tissue>, "detailQuery" | "updateMutation"> {
    pagedSearchQuery: QueryResult<PagedResponse<Tissue>>;
    labDashboardQuery: QueryResult<LabDashboardData>;
    distroDashboardQuery: QueryResult<DistroDashboardData>;
    clearedTissuesQuery: QueryResult<Tissue[]>;
    placementQuery: QueryResult<TissuePlacement>;
    initialEvalQuery: QueryResult<InitialEval>;
    tissueSummaryQuery: QueryResult<DistroTissueSummary>;
    invoiceQuery: QueryResult<Invoice | undefined>;
    productCodeQuery: QueryResult<ProductCodeResponse>;

    updateDistroCommentMutation: MutationResult<Tissue, unknown, CommentUpdateRequest>;
    hideTissueMutation: MutationResult<Tissue, unknown, number>;
    updateWithArgMutation: MutationResult<Tissue, unknown, { tissueId: number; data: Operation[] }>;
}

interface IUseTissueQueryParams extends Omit<QueryHookParams, "queryType"> {
    queryType?: QueryType | "labDashboard" | "distroDashboard" | "clearedTissues" | "invoice" | "productCode";
    includeDetails?: boolean;
    includePending?: boolean;
    includeSclera?: boolean;
    searchParams?: Record<string, string>;
}

export const useTissueQuery = (params?: IUseTissueQueryParams): IUseTissueQuery => {
    const tissueId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, put, patch } = useAxios();
    const { selectedTerritoryIds } = useTerritories();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.Tissues);
    const url = Endpoints.Tissues;

    const queryParamsWithArgs: QueryParams = {
        ...queryParams,
        territoryIds: selectedTerritoryIds.join(","),
        includePending: params?.includePending?.toString() ?? "",
        includeSclera: params?.includeSclera?.toString() ?? "",
    };

    const queryType = useMemo(() => {
        if (params?.queryType === "labDashboard") return "labDashboard";
        if (params?.queryType === "distroDashboard") return "distroDashboard";
        if (params?.queryType === "clearedTissues") return "clearedTissues";
        if (params?.queryType === "invoice") return "invoice";
        if (params?.queryType === "productCode") return "productCode";
        return getQueryType(tissueId, params?.queryType);
    }, [params?.queryType, tissueId]);

    const pagedSearchQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<Tissue>>(
                getRequestOptions(
                    url,
                    { ...config, params: { ...config?.params, ...params?.searchParams } },
                    queryParamsWithArgs,
                    sortParams,
                    pageParams
                )
            ),
        queryKey: queryKeys.tissues.pagedList({
            ...queryParamsWithArgs,
            ...pageParams,
            ...sortParams,
            ...params?.searchParams,
        }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<Tissue>({ url: `${url}/${tissueId}`, config }),
        queryKey: queryKeys.tissues.detail(tissueId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!tissueId,
        cacheTime: 2000,
        staleTime: 2000,
    });

    const labDashboardQuery = useGenericQuery({
        queryFn: (config) =>
            get<LabDashboardData>(getRequestOptions(`${url}/lab-dashboard`, config, queryParamsWithArgs)),
        queryKey: queryKeys.dashboards.lab(queryParamsWithArgs),
        placeholderData: {} as LabDashboardData,
        enabled: isAuthorized && queryType === "labDashboard",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const distroDashboardQuery = useGenericQuery({
        queryFn: (config) =>
            get<DistroDashboardData>(getRequestOptions(`${url}/distro-dashboard`, config, queryParamsWithArgs)),
        queryKey: queryKeys.dashboards.distribution(queryParamsWithArgs),
        placeholderData: {} as DistroDashboardData,
        enabled: isAuthorized && queryType === "distroDashboard",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const clearedTissuesQuery = useGenericQuery({
        queryFn: (config) => get<Tissue[]>(getRequestOptions(`${url}/cleared-tissues`, config, queryParamsWithArgs)),
        queryKey: queryKeys.tissues.clearedTissues(queryParamsWithArgs),
        placeholderData: [],
        enabled: isAuthorized && queryType === "clearedTissues",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const placementQuery = useGenericQuery({
        queryFn: (config) => get<TissuePlacement>(getRequestOptions(`${url}/${tissueId}/placement`, config)),
        queryKey: queryKeys.tissues.placement(tissueId),
        placeholderData: {} as TissuePlacement,
        enabled: isAuthorized && params?.includeDetails && queryType === QueryType.Detail && !!tissueId,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const initialEvalQuery = useGenericQuery({
        queryFn: (config) => get<InitialEval>(getRequestOptions(`${url}/${tissueId}/grading`, config)),
        queryKey: queryKeys.tissues.initialEval(tissueId),
        placeholderData: {} as InitialEval,
        enabled: isAuthorized && params?.includeDetails && queryType === QueryType.Detail && !!tissueId,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const tissueSummaryQuery = useGenericQuery({
        queryFn: (config) => get<DistroTissueSummary>(getRequestOptions(`${url}/${tissueId}/summary`, config)),
        queryKey: queryKeys.tissues.summary(tissueId),
        placeholderData: {} as DistroTissueSummary,
        enabled: isAuthorized && params?.includeDetails && queryType === QueryType.Detail && !!tissueId,
        cacheTime: 5000,
        staleTime: 5000,
    });

    const invoiceQuery = useGenericQuery({
        queryFn: (config) => get<Invoice | undefined>(getRequestOptions(`${url}/${tissueId}/invoice`, config)),
        queryKey: queryKeys.tissues.invoice(tissueId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === "invoice",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const productCodeQuery = useGenericQuery({
        queryFn: (config) => get<ProductCodeResponse>(getRequestOptions(`${url}/${tissueId}/product-code`, config)),
        queryKey: queryKeys.tissues.productCode(tissueId),
        placeholderData: {} as ProductCodeResponse,
        enabled: isAuthorized && queryType === "productCode",
        cacheTime: 5000,
        staleTime: 5000,
    });

    const updateDistroCommentMutation = useGenericMutation({
        mutationFn: (data: CommentUpdateRequest) =>
            put<CommentUpdateRequest, Tissue>({ url: `${url}/${data.tissueId}/distro-comments`, data }),
        queryKey: queryKeys.tissues.detail(tissueId),
        keysToInvalidate: [queryKeys.dashboards.distribution()],
        disableSuccessToast: true,
    });

    const hideTissueMutation = useGenericMutation({
        mutationFn: (tissueId: number) => put<void, Tissue>({ url: `${url}/${tissueId}/distro-hide` }),
        queryKey: queryKeys.tissues.detail(tissueId),
        keysToInvalidate: [queryKeys.dashboards.distribution(), queryKeys.lists.all()],
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], Tissue>({ url: `${url}/${tissueId}`, data }),
        queryKey: queryKeys.tissues.detail(tissueId),
        keysToInvalidate: [queryKeys.tissues.placement(tissueId)],
    });

    const updateWithArgMutation = useGenericMutation({
        mutationFn: ({ tissueId, data }: { tissueId: number; data: Operation[] }) =>
            patch<Operation[], Tissue>({ url: `${url}/${tissueId}`, data }),
        queryKey: queryKeys.tissues.detail(tissueId),
        keysToInvalidate: [queryKeys.dashboards.distribution(), queryKeys.lists.all()],
    });

    return useMemo<IUseTissueQuery>(
        () => ({
            pagedSearchQuery,
            detailQuery,
            labDashboardQuery,
            distroDashboardQuery,
            clearedTissuesQuery,
            placementQuery,
            initialEvalQuery,
            tissueSummaryQuery,
            invoiceQuery,
            productCodeQuery,
            updateDistroCommentMutation,
            hideTissueMutation,
            updateMutation,
            updateWithArgMutation,
        }),
        [
            pagedSearchQuery,
            detailQuery,
            labDashboardQuery,
            distroDashboardQuery,
            clearedTissuesQuery,
            placementQuery,
            initialEvalQuery,
            tissueSummaryQuery,
            invoiceQuery,
            productCodeQuery,
            updateDistroCommentMutation,
            hideTissueMutation,
            updateMutation,
            updateWithArgMutation,
        ]
    );
};
